import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { UploadInput } from 'ngx-uploader';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { ConversationScoreMethod } from 'src/app/modules/conversations/enums/ConversationScoreMethod';
import { ConversationSetupType } from 'src/app/modules/conversations/enums/ConversationSetupType';
import { ConversationTemplateRole } from 'src/app/modules/conversations/enums/ConversationTemplateRole';
import { CurrentConversationSectionItem } from 'src/app/modules/conversations/models/CurrentConversationSectionItem';
import { CurrentConversationSectionItemChosenItem } from 'src/app/modules/conversations/models/CurrentConversationSectionItemChosenItem';
import { CurrentConversationSectionItemContent } from 'src/app/modules/conversations/models/CurrentConversationSectionItemContent';
import { RatingScale } from 'src/app/modules/conversations/models/RatingScale';
import { RatingScaleLevel } from 'src/app/modules/conversations/models/RatingScaleLevel';
import { ConversationService } from 'src/app/modules/conversations/services/conversation.service';
import { CommentKind } from 'src/app/modules/development-objectives/models/DevelopmentObjective';
import { Student } from 'src/app/modules/lms/learningpaths/models/interfaces';
import { DialogService } from 'src/app/shared/dialogs/services/DialogService';
import { LabelContext } from 'src/app/shared/framework/labels/models/LabelContext';
import { LabelService } from 'src/app/shared/framework/labels/services/label.service';
import { Employee } from 'src/app/shared/personnel-files/models/Employee';
import { EmployeeService } from 'src/app/shared/personnel-files/services/employee.service';
import { ICompanyObjective } from '../../models/CompanyObjective';
import { ICompanyObjectiveSettings } from '../../models/CompanyObjectiveSettings';
import { IObjective, ObjectiveComment, ObjectiveStatus } from '../../models/Objective';
import { ObjectiveCategory } from '../../models/ObjectiveCategory';
import { ObjectiveRights } from '../../models/ObjectiveRights';
import { IObjectiveSettings } from '../../models/ObjectiveSettings';
import { ObjectivesPerCategory } from '../../models/ObjectivesPerCategory';
import { ObjectiveType } from '../../models/ObjectiveType';
import { PerformanceObjective } from '../../models/PerformanceObjective';
import { ReviewPeriod } from '../../models/ReviewPeriod';
import { EmployeeObjectiveStatisticsServiceService } from '../../services/employee-objective-statistics-service.service';
import { EmployeePerformanceObjectivesSingletonService } from '../../services/employee-performance-objectives-singleton.service';
import { ObjectiveService } from '../../services/objective-service';

@Component({
  selector: 'app-employee-performance-objectives',
  templateUrl: './employee-performance-objectives.component.html',
  styleUrls: ['./employee-performance-objectives.component.scss'],
})
export class EmployeePerformanceObjectivesComponent implements OnInit, OnDestroy, AfterViewInit {
  public ConversationScoreMethod = ConversationScoreMethod;
  public ConversationTemplateRole = ConversationTemplateRole;

  public allCollapsed = false;
  public allPreviousObjectivesCollapsed = false;
  public allDeletedObjectivesCollapsed = false;
  public collapseGoals = false;
  public collapseDeletedGoals = false;
  public isManager: boolean = false;
  public isHr: boolean = false;
  public showWeight: boolean = false;
  public requiredWeight: boolean = false;
  public activeListItemIndex: number | null = null;
  public showCompanyObjectiveDetail: boolean = false;
  public sort = { column: 1, dir: true };
  public sortCompanyObjectives = { column: 1, dir: true };
  public detailEditable = true;

  public get rights(): ObjectiveRights {
    return this.employeePerformanceObjectivesSingletonService.rights;
  };
  public get settings(): IObjectiveSettings {
    return this.employeePerformanceObjectivesSingletonService.settings;
  };
  public get categories(): ObjectiveCategory[] {
    return this.employeePerformanceObjectivesSingletonService.categories;
  }
  public get statusses(): ObjectiveStatus[] {
    return this.employeePerformanceObjectivesSingletonService.statusses;
  }
  public get types(): ObjectiveType[] {
    return this.employeePerformanceObjectivesSingletonService.types;
  }

  public availableCategories: ObjectiveCategory[] = [];
  public linkableObjectives: IObjective[] = [];
  public objectiveComments: ObjectiveComment[] = [];
  public objectiveCommentAdding: ObjectiveComment;
  public companyObjectives: ICompanyObjective[];
  public CommentKind = CommentKind;

  public activeObjective: PerformanceObjective;
  public activeCompanyObjective: ICompanyObjective;
  public performanceObjectives: PerformanceObjective[];
  public previousObjectives: PerformanceObjective[];
  public deletedObjectives: PerformanceObjective[];

  public objectiveEditing: PerformanceObjective = new PerformanceObjective();

  /** The employee whose goals are displayed */
  public _studentId: number;
  public student: Employee;

  public isEmployee: boolean = false;
  public isCHR: boolean = false;
  public isLHR: boolean = false;
  public isSupervisor: boolean = false;
  public isDelegate: boolean = false;
  public isMatrixManager: boolean = false;
  public isN2: boolean = false;

  public currentEmployee: Student;
  public devSettings: ICompanyObjectiveSettings;

  /** The type of user currently logged on */
  public whoisKind: number;
  public uploadInput: EventEmitter<UploadInput>;

  public currentReviewPeriod: ReviewPeriod;

  public objectivesPerCategory: ObjectivesPerCategory[] = [];

  public previousObjectivesGrouped: {
    previousObjectivesList: PerformanceObjective,
    previousObjectives: PerformanceObjective[],
    year: number
  }[] = [];

  public deletedObjectivesGrouped: {
    deletedObjectivesList: PerformanceObjective,
    deletedObjectives: PerformanceObjective[],
    year: number
  }[] = [];

  public componentWidth: number = 99999999;

  @Input() studentId: number;
  @Input() smallLayout: boolean = false;
  @Input() disableNegativeMargin = false;
  @Input() minGoals: number = 0;
  @Input() maxGoals: number = 999;
  @Input() completedConversation: number = null;
  @Input() conversationId: number = null;
  @Input() conversationSectionItem: CurrentConversationSectionItem = null;
  @Input() conversationGoals: CurrentConversationSectionItemChosenItem[];
  @Input() conversationTemplateRole: ConversationTemplateRole;
  @Input() enableScoring: boolean = false;
  @Input() scoringIsMandatory: boolean = false;
  @Input() scoreMethod?: ConversationScoreMethod;
  @Input() customScoringScale?: RatingScale;
  @Input() customScoringPercentage?: number;
  @Input() currentUserLanguage: number;
  @Input() content: CurrentConversationSectionItemContent[];

  @Output() goalCountChanged = new EventEmitter<number>();
  @Output() newChosenPerformanceObjective = new EventEmitter<PerformanceObjective>();
  @Output() performanceObjectiveScored = new EventEmitter<PerformanceObjective[]>();

  @ViewChild('container') containerRef!: ElementRef<HTMLDivElement>;

  private subscriptions: Subscription[] = [];
  private _labelContext: LabelContext;
  private modalRef: NgbModalRef;

  constructor(
    private employeeService: EmployeeService,
    private objectiveService: ObjectiveService,
    private toastr: ToastrService,
    private _labelService: LabelService,
    private conversationService: ConversationService,
    private modalService: NgbModal,
    private activatedRoute: ActivatedRoute,
    private dialogService: DialogService,
    private _change: ChangeDetectorRef,
    private employeeObjectiveStatisticsService: EmployeeObjectiveStatisticsServiceService,
    private employeePerformanceObjectivesSingletonService: EmployeePerformanceObjectivesSingletonService
  ) {
    this.uploadInput = new EventEmitter<UploadInput>();
  }

  async ngOnInit() {
    this._labelContext = await firstValueFrom(this._labelService.getContext('general'));
    this.subscriptions.push(
      this.objectiveService.getCurrentPeriod(this.enableScoring).subscribe((data) => {
        this.currentReviewPeriod = data;
        this.getData();
        this._change.markForCheck();
      })
    );
  }

  ngAfterViewInit(): void {
    this.updateWidth();

    try {
      window.scrollTo(0, 0);
      window.parent.scrollTo(0, 0);
      window.parent.parent.document.getElementsByClassName('modal-body')[0].scrollTo(0, 0);
    }
    catch (e) { }
  }

  public saveEmployeeRights(event) {
    var element = event.target;
    element.getBoundingClientRect();

    this.subscriptions.push(
      this.objectiveService.saveEmployeeRights(this._studentId, this.rights).subscribe(() => {
        let label = this._labelContext.get('savedsuccessfully').labelText;
        this.toastr.clear();
        this.toastr.success(label, undefined, { timeOut: 5000 });
      })
    );
  }

  // Collapse all list items 
  public collapseAll(): void {
    this.allCollapsed = !this.allCollapsed;
    this.collapseGoals = this.allCollapsed;
    this.collapseDeletedGoals = this.allCollapsed;

    this.collapseAllDeletedObjectives();
    this.collapseAllPreviousObjectives();
    this.resize();

    this._change.markForCheck();
  }

  public collapseAllPreviousObjectives(): void {
    this.allPreviousObjectivesCollapsed = !this.allPreviousObjectivesCollapsed;
    for (const objectives of this.previousObjectives) {
      objectives.previousObjectivesCollapse = this.allPreviousObjectivesCollapsed;
    }
    setTimeout(() => this.resize(), 250);
    this._change.markForCheck();
  }

  // Collapse deleted objectives list
  public collapseAllDeletedObjectives(): void {
    this.allDeletedObjectivesCollapsed = !this.allDeletedObjectivesCollapsed;
    for (const objectives of this?.deletedObjectives) {
      objectives.deletedObjectivesCollapse = this.allDeletedObjectivesCollapsed;
    }
    setTimeout(() => this.resize(), 250);
    this._change.markForCheck();
  }

  // Collapse either previous objectives or deleted objectives 
  public toggleCollapse(index: number, itemType: string): void {

    if (itemType === 'previous_goals') {
      this.collapseGoals = !this.collapseGoals;
    } else if (itemType === 'deletedGoals') {
      this.collapseDeletedGoals = !this.collapseDeletedGoals;
    }

    this.activeListItemIndex = this.activeListItemIndex === index ? null : index;

    setTimeout(() => this.resize(), 250);
    this._change.markForCheck();
  }

  //Toggle the collapse of previous objectives
  public togglePreviousObjectiveCollapse(previousObjective: PerformanceObjective): void {
    previousObjective.previousObjectivesCollapse = !previousObjective.previousObjectivesCollapse;
    setTimeout(() => this.resize(), 250);
    this._change.markForCheck();
  }

  //Toggle the collapse of deleted objectives
  public toggleDeletedObjectiveCollapse(deletedObjective: PerformanceObjective): void {
    deletedObjective.deletedObjectivesCollapse = !deletedObjective.deletedObjectivesCollapse;
    setTimeout(() => this.resize(), 250);
    this._change.markForCheck();
  }

  // Opens the add objectives modal
  public openAddObjectiveModal(modalContent) {
    try {
      window.scrollTo(0, 0);
      window.parent.scrollTo(0, 0);
      window.parent.parent.document.getElementsByClassName('modal-body')[0].scrollTo(0, 0);
      window.parent.parent.parent.scrollTo(0, 0);
    }
    catch (e) { }
    this.objectiveEditing = new PerformanceObjective();
    this.objectiveEditing.typeId = 2;
    this.objectiveEditing.status = 0;

    this.objectiveEditing.categoryId = this.getAvailableCategories(0)?.[0]?.id;


    this.modalRef = this.modalService.open(modalContent, { backdrop: 'static', scrollable: true, size: 'lg' });
    this.modalRef.result.then(() => {
      this.objectiveEditing.studentId = this._studentId;
    }, () => {
      this.objectiveEditing = new PerformanceObjective();
    })
  }

  public addObjectivePerCategory(modalContent, categoryId: number) {
    try {
      window.scrollTo(0, 0);
      window.parent.scrollTo(0, 0);
      window.parent.parent.document.getElementsByClassName('modal-body')[0].scrollTo(0, 0);
      window.parent.parent.parent.scrollTo(0, 0);
    }
    catch (e) { }

    this.objectiveEditing = new PerformanceObjective();
    this.objectiveEditing.typeId = 2;
    this.objectiveEditing.status = 1;
    this.objectiveEditing.reviewPeriodId = this.currentReviewPeriod?.reviewPeriodId;

    if (this.settings.allowCategories) {
      for (let cat of this.categories) {
        if (cat.id == categoryId) {
          this.objectiveEditing.categoryName = cat.name;
        }
      }

    }


    this.modalRef = this.modalService.open(modalContent, { backdrop: 'static', scrollable: true, size: 'lg' });
    this.modalRef.result.then(() => {
      this.objectiveEditing.studentId = this._studentId;
    }, () => {
      this.objectiveEditing = new PerformanceObjective();
    })
  }

  // edit modal
  public async editPerformanceObjective(modalContent, performanceObjective: PerformanceObjective) {
    this.activeObjective = performanceObjective;
    this.availableCategories = this.getAvailableCategories(performanceObjective.categoryId);

    this.objectiveEditing = new PerformanceObjective();
    this.objectiveEditing.typeId = 2;
    this.objectiveEditing.status = 1;

    if (this.settings.allowCategories) {
      const categories = this.getAvailableCategories(0);

      if (categories.length) {
        this.objectiveEditing.categoryId = categories[0].id;
      }
    }

    this.modalService.open(modalContent, { backdrop: 'static', scrollable: true, size: 'xl' }).result.then(() => {
      // Never called, save button has other function: XXX
      this.objectiveEditing.studentId = this._studentId;

    }, () => { });
  }

  public cloneObjective(modalContent, objective) {
    this.objectiveEditing = Object.assign({}, objective);
    this.objectiveEditing.attachments = [];

    this.modalRef = this.modalService.open(modalContent, { backdrop: 'static', scrollable: true, size: 'lg' });
    this.modalRef.result.then(() => {
      this.objectiveEditing.studentId = this._studentId;
    }, () => {
      this.objectiveEditing = new PerformanceObjective();
    });
  }

  // show details of a company objective
  public openCompanyObjectiveDetail(companyObjective: ICompanyObjective) {
    this.activeCompanyObjective = companyObjective;
    this.showCompanyObjectiveDetail = true;

    this.subscriptions.push(
      this.objectiveService.getCompanyObjectiveAttachments(companyObjective.id, this.completedConversation).subscribe((ot) => {
        this.activeCompanyObjective.attachments = ot;
        this._change.markForCheck();
      })
    );

    this.resize();
  }

  // Shows details of an objective
  public openObjectiveDetail(performanceObjective: PerformanceObjective) {
    this.showCompanyObjectiveDetail = false;
    this.detailEditable = true;
    this.activeObjective = performanceObjective;

    this.subscriptions.push(
      this.objectiveService.getObjectiveAttachments(performanceObjective.objectiveId, this.completedConversation).subscribe((ot) => {
        this.activeObjective.attachments = ot;
        this._change.markForCheck();
      })
    );

    this.subscriptions.push(
      this.objectiveService.getComments(performanceObjective.objectiveId, this.completedConversation).subscribe(oc => {
        this.objectiveComments = oc;
        this._change.markForCheck();
      })
    );

    this.objectiveCommentAdding = new ObjectiveComment();
    this.objectiveCommentAdding.commentKind = CommentKind.PrivateComment;

    this.resize();
    this._change.markForCheck();
  }

  // Check whether the limit is reached or not
  public isButtonDisabled(category: any): boolean {
    return category.value?.totalObjectivesPerCategory === category.value.category?.maxNumber ||
      category.value?.totalWeightPerReviewPeriod >= category.value?.maxWeight;
  }

  // Check whether the limit is reached or not with muliple aspects
  public getTooltipMessage(category: any): string {
    let messageCategoryReached = this._labelContext.get('total_goals_per_category_is_reached').labelText;
    let messageWeightReached = this._labelContext.get('total_weight_per_category_is_reached').labelText;
    let messageCategoryAndWeightReached = this._labelContext.get('total_categories_and_weights_are_reached').labelText;

    if (!this.settings.limitedOnCategory)
      return "";

    if (category.value?.totalObjectivesPerCategory === category.value.category?.maxNumber &&
      category.value?.totalWeightPerReviewPeriod >= category.value?.maxWeight) {
      return messageCategoryAndWeightReached;
    }
    if (category.value?.totalObjectivesPerCategory === category.value.category?.maxNumber) {
      return messageCategoryReached;
    }
    if (category.value?.totalWeightPerReviewPeriod >= category.value?.maxWeight) {
      return messageWeightReached;
    }
    return "";
  }

  // Shows deleted objectives information in the side bar
  public openDeletedObjectiveDetail(performanceObjective: PerformanceObjective) {
    this.openObjectiveDetail(performanceObjective);
    this.detailEditable = false;
    this._change.markForCheck();
  }

  //Changes the sorting in asc or desc order.
  public changeSort(column: number) {
    if (this.sort.column == column) {
      this.sort.dir = !this.sort.dir;
    } else {
      this.sort.column = column;
      this.sort.dir = false;
    }

    this.iterator();
    this._change.markForCheck();
  }


  //Changes the sorting in asc or desc order.
  public changeSortCompanyObjectives(column: number) {
    if (this.sortCompanyObjectives.column == column) {
      this.sortCompanyObjectives.dir = !this.sortCompanyObjectives.dir;
    } else {
      this.sortCompanyObjectives.column = column;
      this.sortCompanyObjectives.dir = false;
    }

    this.iteratorCompanyObjectives();
    this._change.markForCheck();
  }

  // Deletes a performance objective
  public async deletePerformanceObjective(objective: PerformanceObjective) {
    let message = (await firstValueFrom(this._labelService.getLabel("action_cannot_be_undone", "general"))).labelText;
    let title = (await firstValueFrom(this._labelService.getLabel("are_you_sure", "general"))).labelText;

    this.dialogService.confirm({ message: message, title: title, centered: false })
      .then(() => {
        this.subscriptions.push(
          this.objectiveService.deleteObjective(objective.objectiveId).subscribe(() => {
            let label = this._labelContext.get('savedsuccessfully').labelText;
            this.toastr.clear();
            this.toastr.success(label, undefined, { timeOut: 5000 });

            this.activeObjective = null;

            this.getPerformanceGoals(this._studentId);
            this.resize();
            this._change.markForCheck();
          })
        );
      }, () => { });
  }

  public async addObjective(updated: PerformanceObjective) {
    this.subscriptions.push(
      this.objectiveService.addEmployeePerformanceObjective(updated, this._studentId, this.enableScoring).subscribe(async (id) => {
        const newAttachments = updated?.attachments?.filter(u => u.file != undefined);

        if (newAttachments?.length > 0) {
          let form: FormData = new FormData();

          form.set("objectiveId", id.toString());

          newAttachments.forEach(u => form.append("files", u.file));
          await firstValueFrom(this.objectiveService.uploadObjectiveAttachments(form));
        }

        if (this.conversationGoals && this.conversationGoals.length) {
          let newChosenItem = new CurrentConversationSectionItemChosenItem();
          newChosenItem.itemId = id;
          newChosenItem.approved = true;
          newChosenItem.name = updated.name;
          newChosenItem.conversationSectionItemId = this.conversationSectionItem.conversationSectionItemId;
          newChosenItem.createdById = this.currentEmployee.studentId;
          newChosenItem.createdByKind = this.conversationTemplateRole;

          this.subscriptions.push(
            this.conversationService.addNewChosenItem(this.conversationId, this.conversationSectionItem.conversationTemplateVersionSectionItemId, newChosenItem, this.conversationTemplateRole, ConversationSetupType.PerformanceObjectives).subscribe(() => {

              this.conversationGoals.push(newChosenItem);
              this.goalCountChanged.emit(this.conversationGoals?.length || 0);
              this.getPerformanceGoals(this._studentId);
            })
          );

        } else {
          await this.getPerformanceGoals(this._studentId);
          this.goalCountChanged.emit(this.settings.allowCategories ? this.objectivesPerCategory.reduce((a, b) => a + b.objectives.length, 0) : this.performanceObjectives.length);
        }

        this.toastr.clear();
        this.toastr.success(
          (await firstValueFrom(this._labelService.getLabel("savedsuccessfully", "general"))).labelText, undefined, { timeOut: 5000 }
        );
      })
    );
  }

  // Save a performance objective
  public savePerformanceObjective(old: PerformanceObjective, updated: PerformanceObjective) {

    this.subscriptions.push(
      this.objectiveService.updateEmployeePerformanceObjective(updated).subscribe(async () => {
        //Delete / upload attachments
        const deletedAttachments = old?.attachments?.filter(a => !updated?.attachments?.find(u => u.objectiveAttachmentId == a.objectiveAttachmentId || u.file));

        //thanks... back-end...
        await deletedAttachments.forEach(async d => {
          await firstValueFrom(this.objectiveService.deleteObjectiveAttachment(d.objectiveAttachmentId))
        });

        const newAttachments = updated?.attachments?.filter(u => u.file != undefined);

        //upload!
        if (newAttachments?.length > 0) {
          let form: FormData = new FormData();

          form.set("objectiveId", old.objectiveId.toString());

          newAttachments.forEach(u => form.append("files", u.file));
          await firstValueFrom(this.objectiveService.uploadObjectiveAttachments(form));
        }

        this.getPerformanceGoals(this._studentId);

        this.toastr.clear();
        this.toastr.success(
          (await firstValueFrom(this._labelService.getLabel("savedsuccessfully", "general"))).labelText, undefined, { timeOut: 5000 }
        );
      })
    );
  }

  // Returns all available categories that are not fully occupied
  public getAvailableCategories(categoryId: number) {
    return this.categories.filter((c) => (!c.isMaxCountReached && !c.isMaxWeightReached) || c.id === categoryId);
  }

  public closeModal() {
    this.modalService.dismissAll();
    this.getPerformanceGoals(this._studentId);
    this._change.markForCheck();
  }

  public addComment(objective: PerformanceObjective) {
    this.objectiveCommentAdding.objectiveId = objective.objectiveId;
    this.objectiveCommentAdding.studentId = objective.studentId;
    this.objectiveCommentAdding.createdById = this.currentEmployee.studentId;
    this.objectiveCommentAdding.createdByKind = this.whoisKind;
    this.objectiveCommentAdding.viewableByAdmin = true;
    this.objectiveCommentAdding.viewableByMgr = true;
    this.objectiveCommentAdding.viewableByStudent = true;

    this.subscriptions.push(
      this.objectiveService.addComment(this.objectiveCommentAdding).subscribe(() => {
        this.subscriptions.push(
          this.objectiveService.getComments(objective.objectiveId, this.completedConversation).subscribe(oc => {
            this.objectiveComments = oc;
            this.objectiveCommentAdding = new ObjectiveComment();
            this.objectiveCommentAdding.commentKind = CommentKind.PrivateComment;
            this._change.markForCheck();
          })
        );
      })
    );

    this._change.markForCheck();
  }

  // Deletes a log in the side bar
  public deleteLogItem(comment: ObjectiveComment) {
    let objectiveId = comment.objectiveId;
    let message = this._labelContext.get('action_cannot_be_undone').labelText;
    let title = this._labelContext.get('are_you_sure').labelText;

    this.dialogService.confirm({ message: message, title: title, centered: false })
      .then(
        () => {
          this.subscriptions.push(
            this.objectiveService.deleteComment(comment.objectiveCommentId).subscribe(() => {
              this.subscriptions.push(
                this.objectiveService.getComments(objectiveId, this.completedConversation).subscribe(oc => {
                  this.objectiveComments = oc;
                  this._change.markForCheck();
                })
              );
            })
          );
        }, () => { });

    this._change.markForCheck();
  }

  public linkObjective(objective: IObjective, event) {
    var linkedObjectiveId = 0;
    if (event.target.checked) {
      linkedObjectiveId = objective.objectiveId;
    }

    var element = event.target;
    const { top, left } = element.getBoundingClientRect();

    this.subscriptions.push(
      this.objectiveService.linkObjective(this.activeObjective.objectiveId, linkedObjectiveId).subscribe(() => {
        this.activeObjective.linkedObjectiveId = objective.objectiveId;
        let label = this._labelContext.get('savedsuccessfully').labelText;
        this.toastr.clear();
        this.toastr.success(label, undefined, { timeOut: 5000 })

        this._change.markForCheck();
      })
    );
  }

  // This opens a modal to view all linked objectives of the employee
  public showCascade(objectiveId: number) {
    let url = "/perfnav/student/ML/common/performance/viewcascade.asp?item=" + objectiveId;

    if (this.isHr) {
      (<any>window.top).openOverlayWide(url);
    } else {
      (<any>window.top).openOverlay(url);
    }
  }

  public downloadAttachment(attachment: any): void {
    const blob: Blob = this.convertToBlob(attachment.fileData, attachment.contentType);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.setAttribute('style', 'display: none');
    a.href = url;
    a.download = attachment.fileName;
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  public isInCurrentConversation(objectiveId: number): boolean {
    return !this.conversationGoals || !this.conversationGoals.length || this.conversationGoals.filter(e => e.itemId == objectiveId).length > 0;
  }

  public getRatingTooltip(level: RatingScaleLevel) {
    return level.translations.filter(e => e.languageId == this.currentUserLanguage)[0]?.name;
  }

  public saveObjectiveScore(objective: PerformanceObjective, level: RatingScaleLevel, objectives: PerformanceObjective[]) {
    objective.score = level.sortOrder;

    let allObjectives = this.settings.allowCategories ? this.objectivesPerCategory.reduce((a, b) => a.concat(b.objectives), []) : this.performanceObjectives;
    allObjectives.find(e => e.objectiveId == objective.objectiveId).score = level.sortOrder;
    this.performanceObjectiveScored.next(allObjectives);
  }

  public objectiveScored() {
    let allObjectives = this.settings.allowCategories ? this.objectivesPerCategory.reduce((a, b) => a.concat(b.objectives), []) : this.performanceObjectives;
    this.performanceObjectiveScored.next(allObjectives);
  }

  public getRoleName(role: ConversationTemplateRole) {
    return this.conversationService.getRoleName(role);
  }

  public getRoleUserLevel(role: ConversationTemplateRole) {
    return this.conversationService.getRoleUserLevel(role);
  }

  public minimumReached(objectivesPerCategory: ObjectivesPerCategory) {
    return objectivesPerCategory && (objectivesPerCategory?.statistics?.minNumber >= objectivesPerCategory.totalObjectives);
  }

  public checkWhoCanSeeComment(comment: ObjectiveComment): boolean {
    const isCreator =
      comment.createdById === this.currentEmployee.studentId &&
      comment.createdByKind === this.whoisKind;

    if (comment.commentKind === CommentKind.PrivateComment) {
      return isCreator;
    }
    if (comment.commentKind === CommentKind.PrivateCommentSharedWithinOwnRole) {
      if (comment.createdByKind === this.whoisKind) {
        return true;
      }
    }

    if (comment.commentKind === CommentKind.PrivateCommentSharedBetweenManagersOrHr) {
      const userIsHr = this.isCHR || this.isLHR;
      const userIsManager = this.isSupervisor || this.isDelegate || this.isMatrixManager || this.isN2;

      const creatorIsHr = comment.createdByKind === 1 || comment.createdByKind === 2;
      const creatorIsManager = comment.createdByKind === 33 || comment.createdByKind === 333 || comment.createdByKind === 3333 || comment.createdByKind === 4;

      if (userIsHr && creatorIsHr) {
        return true;
      }

      if (userIsManager && creatorIsManager) {
        return true;
      }

      return false;
    }

    if (comment.commentKind == CommentKind.SharedComment) {
      return true;
    }

    return false;
  }

  public getCommentKindLabel(comment: ObjectiveComment): string {
    switch (comment.commentKind) {
      case CommentKind.PrivateComment:
        return 'comment_setting_1';

      case CommentKind.PrivateCommentSharedWithinOwnRole:
        if (this.isCHR) {
          return 'private_comment_shared_with_others_hr_1';
        }
        if (this.isLHR) {
          return 'private_comment_shared_with_others_hr_2';
        }
        if (this.isSupervisor) {
          return 'private_comment_shared_with_others_1';
        }
        if (this.isDelegate) {
          return 'private_comment_shared_with_others_2';
        }
        if (this.isMatrixManager) {
          return 'private_comment_shared_with_others_3';
        }
        if (this.isN2) {
          return 'private_comment_shared_with_others_4';
        }

      case CommentKind.PrivateCommentSharedBetweenManagersOrHr:
        if (this.isCHR) {
          return 'comment_setting_shared_with_HR_1';
        }
        if (this.isLHR) {
          return 'comment_setting_shared_with_HR_2';
        }
        if (this.isSupervisor) {
          return 'comment_setting_shared_with_manager_1';
        }
        if (this.isDelegate) {
          return 'comment_setting_shared_with_manager_2';
        }
        if (this.isMatrixManager) {
          return 'comment_setting_shared_with_manager_3';
        }
        if (this.isN2) {
          return 'comment_setting_shared_with_manager_4';
        }

      case CommentKind.SharedComment:
        return 'comment_setting_4';

      default:
        return 'comment_setting_1';
    }
  }

  @HostListener('window:resize')
  resize() {
    setTimeout(() => {
      try {
        (<any>window.top).initIframeResize();
      } catch (e) { }
    }, 500);

    this.updateWidth();
  }

  ngOnDestroy() {
    for (const subsc of this.subscriptions) {
      subsc.unsubscribe();
    }
  }

  private updateWidth() {
    this.componentWidth = this.containerRef.nativeElement.getBoundingClientRect().width;
  }

  private getRoles() {
    if (localStorage.getItem('corporatehr_view') === '1') {
      this.isCHR = true;
    }
    if (localStorage.getItem('localhr_view') === '1') {
      this.isLHR = true;
    }
    if (localStorage.getItem('manager_view') === '1') {
      this.isSupervisor = true;
    }
    if (localStorage.getItem('n2_capital') === '1') {
      this.isN2 = true;
      this.isSupervisor = false;
    }
    if (localStorage.getItem('matrixmanager_view') === '1') {
      this.isMatrixManager = true;
      this.isSupervisor = false;
    }
    if (localStorage.getItem('delegate_view') === '1') {
      this.isDelegate = true;
      this.isSupervisor = false;
    }
  }

  private computeWhoisKindFromView(): number {
    if (this.isCHR) return 1;
    if (this.isLHR) return 2;
    if (this.isN2) return 4;
    if (this.isMatrixManager) return 3333;
    if (this.isDelegate) return 333;
    if (this.isSupervisor) return 33;
    return 3;
  }

  private async getData() {
    this.isManager = localStorage.getItem('manager_view') === '1' || localStorage.getItem('delegate_view') === '1';
    this.isHr = localStorage.getItem('localhr_view') === '1' || localStorage.getItem('corporatehr_view') === '1';
    this.isEmployee = (!this.isManager && !this.isHr);

    this.getRoles();
    this.whoisKind = this.computeWhoisKindFromView();
    const employee: Student = await firstValueFrom(this.employeeService.getCurrentEmployee());

    this.currentEmployee = employee;

    const queryParams: ParamMap = await firstValueFrom(this.activatedRoute.queryParamMap);

    this._studentId = queryParams.has('item') ? parseInt(queryParams.get('item')) : this.studentId ?? 0;

    if (this._studentId == 0) {
      this._studentId = this.studentId ? this.studentId : this.currentEmployee.studentId;
      // this.whoisKind = this.computeWhoisKindFromView();
    } else {
      // let level = 3;
      // switch (employee.level) {
      //     case UserLevelEnum.CorporateHr:
      //         level = 1;
      //         break;
      //     case UserLevelEnum.LocalHr:
      //         level = 2;
      //         break;
      //     case UserLevelEnum.Employee:
      //         level = 3;
      //         break;
      //     case UserLevelEnum.Delegate:
      //         level = 3;
      //         break;
      // }
      // this.whoisKind = this.computeWhoisKindFromView();
    }

    //Settings, rights, etc
    await this.getDependencies();

    //Main student objectives
    this.getPerformanceGoals(this._studentId);

    //Get employee & manager objectives
    this.student = await firstValueFrom(this.employeeService.getEmployee(this._studentId, true));

    if (this.student.manager) {
      this.linkableObjectives = await firstValueFrom(this.objectiveService.getLinkableObjectives(this.student.manager.id));
      this.linkableObjectives = this.linkableObjectives.filter(e => e.allowEmpLinking);
    }

    if (this.settings.allowCompanyGoals) {
      this.companyObjectives = await firstValueFrom(this.objectiveService.getCompanyGoals(this._studentId, this.enableScoring, this.completedConversation));

      this.iteratorCompanyObjectives();
    }

    //Weight settings check
    this.showWeight = (this.isHr && this.settings.weightHR) || (this.isManager && this.settings.weightMgr) || (this.isEmployee && this.settings.weightEmployee);

    this.requiredWeight = this.settings.weightMandatory && this.showWeight;
  }

  private convertToBlob(b64Data: string, contentType: string) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  };

  private getPreviousGoals(studentId: number) {
    this.subscriptions.push(
      this.objectiveService.getPreviousObjectives(studentId).subscribe(preObj => {
        this.previousObjectives = preObj;
        this.filterPreviousObjective();

        this._change.markForCheck();
      })
    );;
  }

  private getDeletedGoals(studentId: number) {
    this.subscriptions.push(
      this.objectiveService.getDeletedObjectives(studentId).subscribe(delObj => {
        this.deletedObjectives = delObj;
        this.filterDeletedObjective();

        this._change.markForCheck();
      })
    );
  }

  private filterPreviousObjective(): void {
    this.previousObjectivesGrouped = [];

    this.previousObjectivesGrouped = this.previousObjectives.reduce((grouping, objective) => {

      if (grouping.filter(e => e.year == objective.reviewYear).length) {
        grouping.filter(e => e.year == objective.reviewYear)[0].previousObjectives.push(objective);
      } else {
        grouping.push({
          previousObjectivesList: this.previousObjectives.filter(x => x.objectiveId == objective.objectiveId)[0],
          previousObjectives: [objective],
          year: objective.reviewYear
        });
      }
      return grouping;

    }, <{ previousObjectivesList: PerformanceObjective, previousObjectives: PerformanceObjective[], year: number }[]>[]);

    this.previousObjectivesGrouped.sort((a, b) => b.year - a.year);

    this._change.markForCheck();
  }

  private filterDeletedObjective(): void {
    this.deletedObjectivesGrouped = [];

    this.deletedObjectivesGrouped = this.deletedObjectives.reduce((grouping, objective) => {

      if (grouping.filter(e => e.year == objective.reviewYear).length) {
        grouping.filter(e => e.year == objective.reviewYear)[0].deletedObjectives.push(objective);
      } else {
        grouping.push({
          deletedObjectivesList: this.deletedObjectives.filter(x => x.objectiveId == objective.objectiveId)[0],
          deletedObjectives: [objective],
          year: objective.reviewYear
        });
      }
      return grouping;

    }, <{ deletedObjectivesList: PerformanceObjective, deletedObjectives: PerformanceObjective[], year: number }[]>[]);
    this.deletedObjectivesGrouped.sort((a, b) => b.year - a.year);

    this._change.markForCheck();
  }

  private async getDependencies() {
    await this.employeePerformanceObjectivesSingletonService.getDependencies(this._studentId);
  }

  // Gets all performance goals 
  private async getPerformanceGoals(studentId: number) {
    //API is seperate, so get catgories, performanceObjectives and statistics in parallel 
    const { objectives, statistics } =
      await firstValueFrom(
        forkJoin({
          objectives: this.objectiveService.getEmployeePerformanceObjectives(studentId, this.enableScoring, this.completedConversation), //Why does this take a studentId?
          statistics: this.objectiveService.getEmployeeStatistics(studentId)
        })
      );

    objectives.sort((a, b) => a.name.localeCompare(b.name));

    if (this.enableScoring) {
      this.loadFeedbackScore(objectives);
    }

    //Set an active objective (if it exists)
    this.activeObjective = objectives?.[0];

    if (this.settings.allowCategories) {
      const categories = Object.assign([], this.employeePerformanceObjectivesSingletonService.categories);

      //API doesn't give us 1 object, so we need to map them together.
      this.objectivesPerCategory = categories.map(category => {
        const statistic = statistics.find(statistic => statistic.id == category.id);

        //For quick lookup, map categories to their id
        this.employeePerformanceObjectivesSingletonService.statisticsMap.set(category?.id, statistic);

        return {
          category: category,
          objectives: objectives.filter(objective => objective.categoryId == category.id),
          statistics: statistic,

          //Depending on the setting get correct weight / total
          ...this.employeeObjectiveStatisticsService.getWeightAndTotalFromStatistic(statistic, this.settings)
        };
      });

      //HACK -> Add dummy category for objectives that do not have a category
      const dummyCategory = new ObjectiveCategory();
      dummyCategory.id = 0;
      dummyCategory.name = (await firstValueFrom(this._labelService.getLabel("performance_objectives_without_category", "goals"))).labelText;
      categories.unshift(dummyCategory);

      this.objectivesPerCategory.unshift({
        category: dummyCategory,
        objectives: objectives.filter(e => !e.categoryId || !categories.find(c => c.id == e.categoryId)),
        statistics: null
      });
    } else {
      this.performanceObjectives = objectives;
    }

    if (!this.enableScoring) {
      // get previous objectives
      this.getPreviousGoals(studentId);

      // get deleted objectives
      if (!this.settings.hideDeletedPerformanceGoals) {
        this.getDeletedGoals(studentId);
      }
    }

    this.iterator();
  }

  private loadFeedbackScore(objectives: PerformanceObjective[]) {
    objectives.forEach(o => {
      if (this.content && this.content.length) {
        const feedbackForObjective = this.content.find(f => f.sourceItemId == o.objectiveId);

        if (feedbackForObjective) {
          o.score = feedbackForObjective.feedback.filter(e =>
            e.createdByKind == this.conversationTemplateRole &&
            e.createdById == this.currentEmployee.studentId
          )[0]?.scorePerItem;

          o.feedback = feedbackForObjective.feedback;

          o.feedback.map(e => {
            e.previousScore = feedbackForObjective.previousScores.filter(p => p.createdById == e.createdById && p.createdByKind == e.createdByKind)[0]?.score ?? null;
            return e;
          });
          o.previousScore = feedbackForObjective.previousScores.filter(e => e.createdById == this.currentEmployee.studentId && e.createdByKind == this.conversationTemplateRole)[0]?.score;
        }
      }
    });

    if (!this.activeObjective && objectives.length) {
      if (this.conversationGoals && this.conversationGoals.length) {
        this.openObjectiveDetail(objectives.filter(e => this.conversationGoals.some(g => g.itemId == e.objectiveId))[0]);
      } else {
        this.openObjectiveDetail(objectives[0]);
      }
    }

  }

  // This function sorts the list from asc to desc 
  private iterator() {
    if (this.settings.allowCategories) {
      this.objectivesPerCategory.forEach(group => {
        this.sortObjectives(group.objectives);
      });
    } else {
      this.sortObjectives(this.performanceObjectives);
    }

    this._change.markForCheck();
  }

  private sortObjectives(objectives: PerformanceObjective[]) {
    objectives.sort((a, b) => {
      const isAsc = !this.sort.dir;
      switch (this.sort.column) {
        case 1:
          return this.compare(a.name, b.name, isAsc);
        case 2:
          return this.compare(a.typeName, b.typeName, isAsc);
        case 3:
          return this.compare(a.categoryName, b.categoryName, isAsc);
        case 4:
          return this.compare(a.statusName, b.statusName, isAsc);
        case 5:
          return this.compare(new Date(a.dueDate || 0).getTime(), new Date(b.dueDate || 0).getTime(), isAsc);
        case 6:
          return this.compare(new Date(a.createdDate || 0).getTime(), new Date(b.createdDate || 0).getTime(), isAsc);
        case 7:
          return this.compare(a.weight, b.weight, isAsc);
        default:
          return 0;
      }
    });
  }

  // This function sorts the list from asc to desc 
  private iteratorCompanyObjectives() {
    let filteredCompanyObjectives = [...this.companyObjectives];

    const isAsc = !this.sortCompanyObjectives.dir;
    filteredCompanyObjectives = filteredCompanyObjectives.sort((a, b) => {
      switch (this.sortCompanyObjectives.column) {
        case 1:
          return this.compare(a.name, b.name, isAsc);
        case 2:
          return this.compare(a.description, b.description, isAsc);
        case 3:
          return this.compare(a.categoryName, b.categoryName, isAsc);
        case 4:
          return this.compare(a.status, b.status, isAsc);
        case 5:
          return this.compare(new Date(a.dueDate || 0).getTime(), new Date(b.dueDate || 0).getTime(), isAsc);
        case 6:
          return this.compare(new Date(a.dateCreated || 0).getTime(), new Date(b.dateCreated || 0).getTime(), isAsc);
        case 7:
          return this.compare(a.weight, b.weight, isAsc);
        default:
          return 0;
      }
    });

    this.companyObjectives = filteredCompanyObjectives;

    this._change.markForCheck();
  }

  private compare(a: number | string, b: number | string, isAsc: boolean = true) {
    if (a && typeof a == 'string') {
      a = a.toLowerCase();
    }
    if (b && typeof b == 'string') {
      b = b.toLowerCase();
    }

    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
